import { ColumnDef, RowSelectionState, TableState } from '@tanstack/react-table';
import {
  createParser,
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  useQueryState,
  useQueryStates,
} from 'nuqs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { View } from '@/components/data-table/views/types';
import { DEFAULT_PAGE_SIZE } from '@/constants';

import { useFilters } from './filters';
import type { FilterDefs, FilterState } from './filters/types';
import { getColumnDefId } from './get-column-def-id';

export type DataTableState = Pick<TableState, 'sorting' | 'pagination' | 'rowSelection'>;

const getColumnSortParser = (columnIds: Set<string>) =>
  createParser({
    parse: (value) => {
      const [id, direction] = value.split(':', 2);
      if (!columnIds.has(id)) {
        return null;
      }
      return { id, desc: direction === 'desc' };
    },
    serialize: (value) => `${value.id}${value.desc ? ':desc' : ''}`,
    eq: (a, b) => a.id === b.id && a.desc === b.desc,
  });

type Props<DataT, TFilterDefs extends FilterDefs = FilterDefs> = {
  columns: ColumnDef<DataT, any>[];
  filterDefs?: TFilterDefs;
  views?: View[];
  isLoadingSavedViews?: boolean;
  defaultPageSize?: number;
  /**
   * Optional prefix for URL params to allow multiple
   * independent tables on one page.
   *
   * For example:
   * `?dashboards_sort=name:desc&reports_sort=name`
   */
  tableId?: string;
};

export function useDataTableState<DataT, TFilterDefs extends FilterDefs>({
  columns,
  filterDefs,
  views,
  isLoadingSavedViews,
  defaultPageSize = DEFAULT_PAGE_SIZE,
  tableId,
}: Props<DataT, TFilterDefs>) {
  const resolvedFilterDefs = useMemo(() => filterDefs ?? ({} as TFilterDefs), [filterDefs]);
  const columnIds = useMemo(
    () => new Set(columns.map((col) => getColumnDefId(col)).filter((x) => x !== undefined)),
    [columns],
  );

  const prefix = tableId ? `${tableId}_` : '';

  const [pagination, setPagination] = useQueryStates(
    {
      pageIndex: parseAsInteger.withDefault(0),
      pageSize: parseAsInteger.withDefault(defaultPageSize),
    },
    { urlKeys: { pageIndex: `${prefix}page`, pageSize: `${prefix}pageSize` } },
  );

  const [sorting, setSorting] = useQueryState(
    `${prefix}sort`,
    parseAsArrayOf(getColumnSortParser(columnIds)).withDefault([]),
  );

  const [viewId, setViewId] = useQueryState('view', parseAsString);

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const currentView = useMemo(() => {
    if (!viewId) {
      // No specific view requested in the URL
      return views?.[0];
    }

    const requestedView = views?.find((v) => v.id === viewId);
    if (requestedView) {
      // Found the requested view (could be preset or saved view)
      return requestedView;
    }

    if (isLoadingSavedViews) {
      // View not found yet, but saved views are still loading.
      // Wait for them to load and don't set a view in the meantime
      // (doing so might unintendedly set filter state too early)
      return undefined;
    }

    // View not found and done loading - fallback to first view
    return views?.[0];
  }, [viewId, views, isLoadingSavedViews]);

  const initialFilterStateRef = useRef(
    currentView?.state?.filters as FilterState<TFilterDefs> | undefined,
  );

  const state = useMemo<DataTableState>(
    () => ({ sorting, pagination, rowSelection }),
    [sorting, pagination, rowSelection],
  );

  const resetPageIndex = useCallback(
    () => setPagination((prev) => ({ ...prev, pageIndex: 0 })),
    [setPagination],
  );

  const resetRowSelection = useCallback(() => setRowSelection({}), []);

  const onFilterInteraction = useCallback(async () => {
    await Promise.all([resetPageIndex(), resetRowSelection()]);
  }, [resetPageIndex, resetRowSelection]);

  const filters = useFilters<TFilterDefs>({
    filterDefs: resolvedFilterDefs,
    initialState: initialFilterStateRef.current,
    onFilterInteraction,
  });

  /**
   * When a view changes, the filters are synced to the filters defined in the view:
   * * The useEffect targets the initial load where the URL specifies a view but no filters. It needs `[currentView]`
   *   as a dependency as views can take time to load after page load.
   * * The onChangeViewId callback handles when the user changes the view. It needs to be done here to avoid a momentary
   *   point where the view is updated but the filters aren't yet (waiting on the effect to run next render).
   */
  useEffect(() => {
    if (currentView && !filters.filterState.length) {
      void filters.setFilterState(currentView.state?.filters ?? []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView]);

  const onChangeViewId = useCallback(
    async (id: string) => {
      void setViewId(id);
      const view = views?.find((v) => v.id === id);
      if (view) {
        void filters.setFilterState(view?.state?.filters ?? []);
        void resetPageIndex();
        resetRowSelection();
      }
    },
    [setViewId, views, filters, resetPageIndex, resetRowSelection],
  );

  return {
    state,
    currentView,
    onChangeViewId,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
    filters,
  };
}
